import React, { useEffect, useState } from "react";
import Link from "next/link";
import { Breadcrumb, Steps } from "antd";
import Image from "next/image";
import { useRouter } from "next/router";
import styles from "@aspen/theme/Profile.module.less";
import {
  EKycType,
  GA_EVENT_NAME,
  i18nUtil,
  kycWebStorage,
  PROFILE_PATHS,
  reportEvent,
  CUSTOMER_ID,
  OPERATE_CUSTOMER_ID,
} from "@aspen/libs";
import type { TKycInfoV2 } from "@adgm/cross-models";
import { EKycStep, IKycStatusV2, typeList } from "@adgm/cross-models";
import { submitKyc } from "@aspen/services";
import { message, WithKYCInfo } from "@aspen/ui";
import {
  InstitutionsBasic,
  ModalResult,
  OnfidoVerificationFlowModal,
  Questionnaire,
} from "@aspen/widgets";
import tips_red from "@aspen/assets/images/tips_red.svg";
import { KycUpload } from "@adgm/cross-ui";
import { LeftOutlined } from "@ant-design/icons";
import { LATEST_KYC_API_VERSION } from "@adgm/cross-config";

interface IProps {
  kycInfo: TKycInfoV2;
}

const Verification: React.FC<IProps> = props => {
  const { kycInfo } = props;
  const router = useRouter();
  const kycType = router.query.type;
  const isIndividual = kycType === "individuals";
  const storageData = kycWebStorage.loadFromLocalStorage();

  const storageStep = storageData[isIndividual ? EKycType.INDIVIDUAL : EKycType.INSTITUTION];
  const intl = i18nUtil.t();

  const [loading, setLoading] = useState<boolean>(false);

  const [currentStep, setCurrentStep] = useState<number>(
    isIndividual && storageStep === EKycStep.VERIFICATION ? EKycStep.QUESTIONNAIRE : storageStep,
  );
  const [showOnfido, setShowOnfido] = useState<boolean>(
    isIndividual && storageStep === EKycStep.VERIFICATION,
  );
  const [showResultModal, setShowResultModal] = useState<boolean>(false);

  useEffect(() => {
    const customerId =
      localStorage.getItem(OPERATE_CUSTOMER_ID) || sessionStorage.getItem(CUSTOMER_ID);
    const formData = kycWebStorage.loadFromLocalStorage()?.formData;
    // 如果存储的表单数据的customerId不匹配当前登录的customerId，或者版本不是V2，则清空表单数据
    if (customerId != formData?.customerId || formData?.version !== LATEST_KYC_API_VERSION) {
      kycWebStorage.clearKycForm();
      setCurrentStep(0);
      setShowOnfido(false);
    }

    // web端的kyc表单的三个步骤在同一路由下,用户在反复切换 个人|机构 表单的时候,可能不会重新初始化问卷表单(第一步骤),导致type字段错误,再此进行校正
    const _storageStep =
      kycWebStorage.loadFromLocalStorage()[
        isIndividual ? EKycType.INDIVIDUAL : EKycType.INSTITUTION
      ];
    kycWebStorage.saveToLocalStorage({
      formData: {
        ...kycWebStorage.loadFromLocalStorage().formData,
        type: isIndividual ? typeList.Individuals : typeList.Institutions,
      },
      isIndividual,
      currentStep: _storageStep,
    });
  }, []);

  // 下一步
  const handleNextStep = () => {
    if (isIndividual) {
      setShowOnfido(true);
      reportEvent({
        moduleName: GA_EVENT_NAME.kyc.submitIndSur,
      });
    } else {
      setCurrentStep(currentStep + 1);
      const gaName =
        currentStep === 0 ? GA_EVENT_NAME.kyc.submitInsSur : GA_EVENT_NAME.kyc.submitInsDoc;
      reportEvent({
        moduleName: gaName,
      });
    }
  };
  // 返回上一步
  const goback = () => {
    const count = isIndividual ? 2 : 1;
    setCurrentStep(currentStep - count);
  };

  // 页面存在Y轴偏移，回到顶部
  const scrollToTopIfNeeded = () => {
    if (window.scrollY > 0) {
      window.scrollTo({
        top: 0,
      });
    }
  };

  // 提交
  const handleSubmit = (files: string[]) => {
    setLoading(true);
    submitKyc({ ...kycWebStorage.loadFromLocalStorage().formData, files })
      .then(res => {
        if (res?.code == "0") {
          if (isIndividual) {
            setShowResultModal(true);
          } else {
            message.success(intl["profile.kyc.successMessage"]);
            //表单提交之后,清理表单
            kycWebStorage.clearKycForm();
            router.replace(PROFILE_PATHS.PROFILE_KYC_RESULT);
          }
        } else {
          message.error(intl[res?.msg ?? ""] ?? res?.msg);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // 表单
  const renderFormView = () => {
    switch (currentStep) {
      case 0:
        return (
          <Questionnaire
            loading={loading}
            kycInfo={kycInfo}
            handleNextStep={handleNextStep}
            isIndividual={isIndividual}
          />
        );
      case 1:
        if (!isIndividual) {
          return <InstitutionsBasic handleGoBack={goback} handleNextStep={handleNextStep} />;
        }
        return undefined;
      default:
        return (
          <>
            <p className={styles.preBtn} onClick={goback}>
              <LeftOutlined className={styles.preBtnIcon} />
              <span className={styles.preBtnText}>{intl["kyc.form.step.previous"]}</span>
            </p>
            <KycUpload
              kycType={router.query.type as "individuals" | "institutions"}
              onSubmit={handleSubmit}
              loading={loading}
            />
          </>
        );
    }
  };

  return (
    <div className={styles.kycFormContainer}>
      <div className={styles.titleContainer}>
        <div className={styles.topContainer}>
          <Breadcrumb>
            <Breadcrumb.Item>{intl["kyc.certify.location"]}:</Breadcrumb.Item>
            <Breadcrumb.Item>
              <Link href={PROFILE_PATHS.PROFILE}>{intl["profile"]}</Link>
            </Breadcrumb.Item>
            <Breadcrumb.Item>
              {isIndividual
                ? intl["kyc.form.title.individual"]
                : intl["kyc.form.title.institutions"]}
            </Breadcrumb.Item>
          </Breadcrumb>
          <div className={styles.titleContent}>
            <span className={styles.titleText}>
              {isIndividual
                ? intl["kyc.form.title.individual"]
                : intl["kyc.form.title.institutions"]}
            </span>
          </div>
          <p className={styles.subitleText}>{intl["kyc.form.title.sub"]}</p>
        </div>
      </div>
      <div className={styles.container}>
        <div className={styles.tipsContent}>
          {kycInfo.reviewReason ? (
            <div className={styles.rejectTips}>
              <Image unoptimized alt="" width={15} height={15} src={tips_red} />
              <span className={styles.rejectTipText}>{kycInfo.reviewReason}</span>
            </div>
          ) : null}
        </div>
        <div className={styles.stepContainer}>
          <Steps
            size="small"
            current={currentStep}
            labelPlacement="vertical"
            items={[
              {
                title: intl["kyc.form.step.title1"],
              },
              {
                title: intl["kyc.form.step.title2"],
              },
              {
                title: isIndividual
                  ? intl["kyc.form.step.title.risk"]
                  : intl["kyc.form.step.title.upload"],
              },
            ]}
          />
        </div>
        <div className={styles.formContainer}>{renderFormView()}</div>
      </div>
      {showOnfido ? (
        <OnfidoVerificationFlowModal
          onModalRequestClose={() => {
            setShowOnfido(false);
          }}
          onComplete={(_country: string) => {
            setShowOnfido(false);
            setCurrentStep(currentStep + 2);
            // 在onfido结束后，页面回到顶部
            scrollToTopIfNeeded();
            kycWebStorage.saveToLocalStorage({
              isIndividual,
              currentStep: EKycStep.UPLOAD,
            });
          }}
        />
      ) : null}
      {showResultModal ? (
        <ModalResult
          open={showResultModal}
          resultType="success"
          textAlign="center"
          title={intl["kyc.form.individual.success.title"]}
          text={intl["kyc.form.individual.success.content"]}
          onOk={() => {
            router.replace(PROFILE_PATHS.PROFILE);
            kycWebStorage.clearKycForm();
          }}
          onCancel={() => {
            router.replace(PROFILE_PATHS.PROFILE);
            kycWebStorage.clearKycForm();
          }}
        />
      ) : null}
    </div>
  );
};

export const PageKycVerification = WithKYCInfo(Verification);
